/* eslint-disable jsx-a11y/click-events-have-key-events */
/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@emotion/core';
import { Row, Col } from 'react-grid-system';
import BlockUI from 'components/molecules/BlockUi';
import FormContents from 'components/atoms/Form/FormContents';
import useYearlyAgreement36Form, { YearlyInfoAgreement36Data } from './hooks';
// import CalendarForm from 'components/molecules/CalendarForm';
import FlexBoxItem from 'components/atoms/FlexBoxItem';
import FlexBox from 'components/atoms/FlexBox';
import FormPadding from 'components/atoms/Form/FormPadding';
import FormLabel from 'components/atoms/Form/FormLabel';
import productColor from 'components/styles';
import SelectForm from "components/molecules/SelectForm";
import Checkbox from 'components/molecules/Checkbox';
import { Column } from 'react-table';
import DataTable from 'components/organismos/DataTable/DataTable';
import TextTooltip from 'components/molecules/TextTooltip';
import ModalDetailStaff from './../ModalDetailStaff'
import Modal from 'components/molecules/Modal';
import BodyText from 'components/atoms/BodyText';
import YearPicker from "components/molecules/YearPicker/YearPicker";
import FormHeader from 'components/templates/FormHeader';
import ClickableIconButton from 'components/atoms/ClickableIconButton';
import Icon from 'components/atoms/Icon';
import moment from 'moment';
import FormField from 'components/atoms/Form/FormField';

const YearlyAgreement36Form: React.FC = () => {
  // アップロードファイル
  const styles = {
    title: css({
      fontSize: "18px",
      color: "#333333",
      fontWeight: 700,
      marginLeft: '0'
    }),
    errText: css({
      color: productColor.deleteAction,
      textDecoration: 'underline',
      fontSize: '14px'
    }),
    alertLabel: css({
      margin: '0 15px'
    }),
    tableContainer: css({
      padding: '15px 35px',
      width: 'calc( 50% - 70px )',
      alignSelf: 'flex-start'
    }),
    tableContent: css`
      td{
        :last-child{
          color: ${productColor.primary}
        }
        text-align: center
      }
    `,
    titleTable: css({
      fontWeight: 'bold',
    }),
    errorNote: css({
      color: productColor.secondary
    }),
    warningNote: css({
      color: productColor.copyAction
    }),
    countLabel: css({
      fontSize: '17px',
      fontWeight: 'bold',
      margin: '0'
    }),
    detailLink: css({
      color: productColor.primary
    }),
    detailText: css({
      color: productColor.primary,
      '&:hover': {
        opacity: 0.8,
        cursor: 'pointer'
      },
      marginRight: '15px'
    }),
    tableStyles: css({
      overflow: 'hidden'
    },css`
      tr td:nth-child(3),
      tr td:nth-child(4),
      tr td:nth-child(5),
      tr td:nth-child(6)
      {
        padding: 0px;
      }
    `)
  };

  const getOverTimeFlag = (flag: number) => {
    let tmpStyle = {
      margin: '0px',
      padding: '14px'
    };
    switch (flag) {
      case 1:
        return { ...tmpStyle, backgroundColor: productColor.copyAction }
      case 2:
        return { ...tmpStyle, backgroundColor: productColor.secondary }
      default:
        return tmpStyle
    }
  };

  const getAveOverHolidayTimeFlag = (flag: number) => {
    let tmpStyle = {
      margin: "0px",
      padding: '14px'
    };
    switch (flag) {
      case 2:
        return { ...tmpStyle, backgroundColor: productColor.secondary }
      default:
        return tmpStyle
    }
  };

  const getStyleLabel = (index: number, maxLength: number) => {
    switch (index) {
      case maxLength - 2:
        return css({ color: productColor.copyAction })
      case maxLength - 1:
        return css({ color: productColor.secondary })
      default:
        return css({})
    }
  };

  const {
    isLoading,
    applyYear,
    setApplyYear,
    orgCode,
    setOrgCode,
    orgTreesOptions,
    checkboxThreshold,
    setCheckboxThreshold,
    yearlyAgreement36Data,
    yearlyInfoData,
    checkboxThresholdCondition,
    setCheckboxThresholdCondition,
    staffDetail,
    isOpenModal,
    setIsOpenModal,
    handleOpenDetail
  } = useYearlyAgreement36Form();

  const columns: Array<Column<YearlyInfoAgreement36Data>> = React.useMemo(() => [
    {
      Header: "スタッフ名",
      accessor: 'x',
      disableSortBy: true,
      id: 'actionButton',
      Cell: (cell: { row: { isExpanded: boolean, original: YearlyInfoAgreement36Data } }) => {
        // eslint-disable-next-line
        return (
          <div style={{display: 'flex', 
                       flexDirection: 'row', 
                       justifyContent: 'space-between', 
                       alignContent: 'center', 
                       alignItems: 'center'}}
          >
            <TextTooltip text={cell.row.original.staffName} textDis={(
              <div className="ellipsis headline-cell-width-120" 
                   style={{textOverflow: 'ellipsis', 
                           whiteSpace: 'nowrap', 
                           overflow: 'hidden',
                           width: '140px'}}
              >
                <span>{cell.row.original.staffName}</span>
              </div>
            )}/>
            <BodyText 
              customStyle={styles.detailText} 
              onClick={() => handleOpenDetail(cell.row.original.staffCode)}
            >
              詳細
            </BodyText>  
          </div>
        );
      }
    },
    {
      Header: '組織名',
      accessor: 'orgName'
    },
    {
      Header: '年法定外時間',
      accessor: 'overTimeYear',
      Cell: (cell: { row: { isExpanded: boolean, original: YearlyInfoAgreement36Data } }) => {
        // eslint-disable-next-line
        return (
          <div css={css(getOverTimeFlag(cell.row.original.overTimeYearFlag))}>
            {cell.row.original.overTimeYearHour}
          </div>
        ) 
      }
    },
    {
      Header: () => (<React.Fragment>月平均残業<br />アラート回数</React.Fragment>),
      accessor: 'aveOverAlertCnt',
      Cell: (cell: { row: { isExpanded: boolean, original: YearlyInfoAgreement36Data } }) => {
        return (
          <div css={css(getAveOverHolidayTimeFlag(cell.row.original.aveOverFlag))}>
            {cell.row.original.aveOverAlertCnt}回
          </div>
        )
      }
    },
    {
      Header: () => (<React.Fragment>{yearlyAgreement36Data.monthOverConditionsHour}時間超過回数<br />（法定外労働）</React.Fragment>),
      accessor: 'overTimeAlertCnt',
      Cell: (cell: { row: { isExpanded: boolean, original: YearlyInfoAgreement36Data } }) => {
        return (
          <div css={css(getOverTimeFlag(cell.row.original.overTimeFlag))}>
            {cell.row.original.overTimeAlertCnt}回
          </div>
        ) 
      }
    },
    {
      Header: () => (<React.Fragment>{yearlyAgreement36Data.monthlyOverAndHolidayTimeHour}時間超過回数<br />（法定外＋休日出勤）</React.Fragment>),
      accessor: 'overHolidayTimeAlertCnt',
      Cell: (cell: { row: { isExpanded: boolean, original: YearlyInfoAgreement36Data } }) => {
        return (
          <div css={css(getAveOverHolidayTimeFlag(cell.row.original.overHolidayTimeFlag))}>
            {cell.row.original.overHolidayTimeAlertCnt}回
          </div>
        ) 
      }
    }
  ], [yearlyAgreement36Data]);

  return (
    <BlockUI blocking={isLoading}>
      <FormHeader title="年次" />
      <FormContents customStyle={css({marginTop:'60px', paddingTop: '7px'})}>
        <FormPadding> 
          <FlexBox>
            <FormLabel label="対象年" customStyle={css({width: '100px', marginRight: '50px'})}/>
            {/* <FlexBoxItem>
              <ClickableIconButton onClick={() => {
                const subtractYear = applyYear - 1;
                setApplyYear(subtractYear);
              }}>
                <Icon type="chevronLeft" color="" size="24px" />
              </ClickableIconButton>
            </FlexBoxItem> */}
            <FlexBoxItem width="160px">
              {/* <CalendarForm 
                required={true} 
                label="" 
                date={applyDate} 
                dateFormat="YYYY/MM" 
                changeDate={(date: Date) => {
                  if(date.getMonth() !== applyDate.getMonth() || date.getFullYear() !== applyDate.getFullYear()){
                    setApplyDate(date)
                  }      
              }}/> */}
              <YearPicker
                openActionChevron={true}
                selectedYear={applyYear}
                onChange={(year: any) => {setApplyYear(year);}}
              />
            </FlexBoxItem>
            {/* <FlexBoxItem customStyle={css({marginLeft: '-60px'})}>
              <ClickableIconButton onClick={() => {
                const addYear = applyYear + 1;
                setApplyYear(addYear);
              }}>
                <Icon type="chevronRight" color="" size="24px" />
              </ClickableIconButton>
            </FlexBoxItem> */}
          </FlexBox>
        </FormPadding>
        <FormField displayBlock={true} customStyle={css({margin: '0px 15px'})}>
          <Row>
            <Col xl={6} md={12} xs={12} >
              <FlexBox>
                <FlexBoxItem width='100px' customStyle={css({paddingRight: '49px'})}>
                  <FormLabel label="組織"/>
                </FlexBoxItem>
                <FlexBoxItem grow={1} width='150px'>
                  <SelectForm
                    label=""
                    name="orgCode"
                    value={String(orgCode)}
                    setValue={(v) => {setOrgCode(v);}}
                    options={orgTreesOptions}
                    description=""
                    ghost={(['3', '2'].indexOf(sessionStorage.getItem('loginUser.site') || "") === -1 && orgTreesOptions.length === 1)}
                  />
                </FlexBoxItem>
              </FlexBox>                
            </Col>
          </Row>
        </FormField>
        <FormPadding>
          <FlexBox>
            <FormLabel label="年法定外時間" customStyle={css({margin: '0', width: '150px'})}/>
            {yearlyAgreement36Data.yearOvertimeHourList?.map((item, index) => (
              <FlexBoxItem customStyle={css({ marginRight: '20px' })}>
                <Checkbox
                  id={`checkboxThreshold${index}`}
                  name={`checkboxThreshold${index}`}
                  label={`${item}時間を超過`}
                  value={`checkboxThreshold${index}`}
                  checked={checkboxThreshold[index]}
                  onChange={() => {
                    let tmpList = [...checkboxThreshold];
                    tmpList[index] = !tmpList[index];
                    setCheckboxThreshold(tmpList);
                  }}
                  customStyleLabel={getStyleLabel(index, yearlyAgreement36Data.yearOvertimeHourList?.length || 0)}
                />
              </FlexBoxItem>
            ))}
          </FlexBox>
        </FormPadding>
        <FormPadding>
          <FlexBox>
            <FormLabel label="届出限度時間超過" customStyle={css({margin: '0', width: '150px'})}/>
            {yearlyAgreement36Data.monthlyOverConditionsCountList?.map((item, index) => (
              <FlexBoxItem customStyle={css({ marginRight: '20px' })}>
                <Checkbox
                  id={`checkboxThresholdCondition${index}`}
                  name={`checkboxThresholdCondition${index}`}
                  label={`${item}回超過`}
                  value={`checkboxThresholdCondition${index}`}
                  checked={checkboxThresholdCondition[index]}
                  onChange={() => {
                    let tmpList = [...checkboxThresholdCondition];
                    tmpList[index] = !tmpList[index];
                    setCheckboxThresholdCondition(tmpList);
                  }}
                  customStyleLabel={getStyleLabel(index, yearlyAgreement36Data.monthlyOverConditionsCountList?.length || 0)}
                />
              </FlexBoxItem>
            ))}
          </FlexBox>
        </FormPadding>
        <div 
          className='table-bordered table-padding' 
          css={css`
            white-space: nowrap;
            overflow-x: auto;
            padding-bottom: 15px;
            table th:nth-of-type(1) {
              width: 170px;
            }
            table td:nth-of-type(1) {
              text-align: left;
            }
            table th:nth-of-type(2) {
              width: 140px;
            }        
            table th:nth-of-type(3) {
              width: 130px;
            }       
            table th:nth-of-type(4) {
              width: 135px;
            }          
            table th:nth-of-type(5) {
              width: 145px;
            }      
            table th:nth-of-type(6) {
              width: 145px;
            }
            table td:nth-of-type(n+3) {
              text-align: center;
            }
          `}
        >
          <DataTable 
            className="table-no-border-left-right"
            classNameParrant="border-left-right"
            columns={columns}
            data={yearlyInfoData || []}
            isGlobalFilter={false}
            minWidth="100%"
            customStyle={styles.tableStyles} 
          />
        </div>
      </FormContents>
      <Modal
        open={isOpenModal}
        closeHandler={() => setIsOpenModal(false)}
        title="時間外労働一覧"
        submitText=""
        closeText="閉じる"
        onSubmit={() => { }}
      >
        {!isLoading && staffDetail && staffDetail.length > 0 && (
          <ModalDetailStaff
            staffDetail={staffDetail}
          />
        )}
      </Modal>
    </BlockUI>
  );
};

export default YearlyAgreement36Form;
